import { Time } from "@internationalized/date";

const MINUTES_PER_DAY = 24 * 60;

/** Minutes since local midnight, wrapped into a single day for display. */
export function clockOf(minute: number): { hour: number; minute: number } {
  const wrapped =
    ((minute % MINUTES_PER_DAY) + MINUTES_PER_DAY) % MINUTES_PER_DAY;
  return { hour: Math.floor(wrapped / 60), minute: wrapped % 60 };
}

/** e.g. `360` -> "6:00 AM". */
export function formatClock(minute: number): string {
  const { hour, minute: min } = clockOf(minute);
  const period = hour < 12 ? "AM" : "PM";
  const twelveHour = hour % 12 === 0 ? 12 : hour % 12;
  return `${twelveHour}:${min.toString().padStart(2, "0")} ${period}`;
}

/**
 * e.g. `(360, 870)` -> "8h 30m". `endMinute` may exceed 1440 for a shift that
 * crosses midnight (see `Shift.endMinute`), so the raw difference -- not a
 * wrapped one -- is the actual duration.
 */
export function formatDuration(startMinute: number, endMinute: number): string {
  const totalMinutes = endMinute - startMinute;
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return minutes === 0 ? `${hours}h` : `${hours}h ${minutes}m`;
}

export function minuteToTimeValue(minute: number): Time {
  const { hour, minute: min } = clockOf(minute);
  return new Time(hour, min);
}

export function timeValueToMinute(value: Time): number {
  return value.hour * 60 + value.minute;
}

/**
 * e.g. `360` -> `"06:00"` -- the `appWarehouseSettings.settings.shiftList`
 * wire format (org_config.yaml's `shiftRecord`), which has no way to express
 * a crossing-midnight `endMinute` past 24:00, so this always wraps into a
 * single day. `endMinuteAfter` recovers the crossing on the way back in.
 */
export function minuteToTimeString(minute: number): string {
  const { hour, minute: min } = clockOf(minute);
  return `${hour.toString().padStart(2, "0")}:${min.toString().padStart(2, "0")}`;
}

/** e.g. `"06:00"` -> `360`. Inverse of `minuteToTimeString`. */
export function timeStringToMinute(value: string): number {
  const [hour, minute] = value.split(":").map(Number);
  return (hour ?? 0) * 60 + (minute ?? 0);
}

/**
 * Resolves an end time typed as earlier than the start into a crossing of
 * midnight (Night: 10:00 PM -> 6:30 AM), rather than a negative duration.
 */
export function endMinuteAfter(
  startMinute: number,
  rawEndMinute: number,
): number {
  return rawEndMinute <= startMinute
    ? rawEndMinute + MINUTES_PER_DAY
    : rawEndMinute;
}
